import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, waShareUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Plus, UserPlus, Building2, Phone, Copy, Edit2, Trash2, Download, ArrowLeftRight, Mail, Calendar, Eye } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";

export default function DirectoryPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ flats: [], users: [] });
  const isCommittee = user?.role === "committee";

  const load = useCallback(async () => {
    const r = await api.get("/directory");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  function userOf(id) { return data.users.find((u) => u.id === id); }

  async function deleteFlat(f) {
    if (!confirm(`Delete flat ${f.label}? This cannot be undone.`)) return;
    try { await api.delete(`/communities/flats/${f.id}`); load(); toast.success("Flat removed"); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  async function changeRole(u, role) {
    try { await api.patch(`/communities/users/${u.id}/role`, { role }); load(); toast.success(`${u.name} is now ${role}`); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Directory</div>
          <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">People & flats</h1>
        </div>
        {isCommittee && (
          <div className="flex gap-2 flex-wrap">
            <AddFlatDialog onDone={load} />
            <BulkFlatsDialog onDone={load} />
            <InvitePersonDialog flats={data.flats} onDone={load} />
            <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/directory`, "_blank")}
              data-testid="dir-export"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-300 text-xs hover:border-stone-500"><Download size={12} /> Export</button>
          </div>
        )}
      </div>

      <h2 className="font-serif-display text-xl font-medium mt-7 mb-3">Flats ({data.flats.length})</h2>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {data.flats.map((f) => (
          <FlatCard key={f.id} flat={f} owner={userOf(f.owner_user_id)} tenant={userOf(f.tenant_user_id)}
            isCommittee={isCommittee} users={data.users} onDone={load} onDelete={deleteFlat} />
        ))}
        {data.flats.length === 0 && <div className="text-stone-500 italic col-span-2 py-4">No flats added yet.</div>}
      </div>

      <h2 className="font-serif-display text-xl font-medium mt-8 mb-3">People ({data.users.length})</h2>
      <div className="space-y-2">
        {data.users.map((u) => (
          <PersonRow key={u.id} person={u} isCommittee={isCommittee} currentUserId={user.id} onChangeRole={changeRole} />
        ))}
      </div>
    </div>
  );
}

function FlatCard({ flat: f, owner, tenant, isCommittee, users, onDone, onDelete }) {
  const phone = owner?.phone || tenant?.phone;
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid={`flat-${f.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-sm font-semibold">{f.label}</div>
        <div className="flex items-center gap-1">
          {phone && (
            <>
              <a href={`tel:${phone}`} className="text-stone-500 hover:text-[#C05640] p-1.5" data-testid={`flat-call-${f.id}`}><Phone size={14} /></a>
              <a href={`https://wa.me/${phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                className="text-[#25D366] hover:opacity-80 p-1.5" data-testid={`flat-wa-${f.id}`}><FaWhatsapp size={16} /></a>
            </>
          )}
          {isCommittee && (
            <>
              <EditFlatDialog flat={f} users={users} onDone={onDone} />
              <TransferOwnerDialog flat={f} users={users} onDone={onDone} />
              <button onClick={() => onDelete(f)} className="text-stone-400 hover:text-red-600 p-1.5" data-testid={`flat-del-${f.id}`}><Trash2 size={14} /></button>
            </>
          )}
        </div>
      </div>
      <div className="text-xs text-stone-500 mt-1.5">{owner ? `Owner: ${owner.name}` : <em className="not-italic text-stone-400">Owner not assigned</em>}</div>
      {tenant && <div className="text-xs text-stone-500">Tenant: {tenant.name}</div>}
    </div>
  );
}

function PersonRow({ person: u, isCommittee, currentUserId, onChangeRole }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-white border border-stone-200 rounded-xl p-3" data-testid={`user-${u.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate flex items-center gap-2">
          {u.name}
          {isCommittee && <PersonDetailDialog uid={u.id} />}
        </div>
        <div className="text-xs text-stone-500 flex items-center gap-2">
          {isCommittee && u.id !== currentUserId ? (
            <select value={u.role} onChange={(e) => onChangeRole(u, e.target.value)} data-testid={`role-${u.id}`}
              className="text-xs border border-stone-300 rounded-full px-2 py-0.5 bg-white capitalize">
              {["owner", "tenant", "family_member", "committee", "staff", "vendor", "ex_resident"].map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>
          ) : <span className="capitalize">{(u.role || "").replace("_", " ")}</span>}
          {u.phone && !u.phone_private && <span>· {u.phone}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {u.phone && !u.phone_private && (
          <>
            <a href={`tel:${u.phone}`} className="text-stone-500 hover:text-[#C05640] p-2"><Phone size={16} /></a>
            <a target="_blank" rel="noreferrer" href={`https://wa.me/${u.phone.replace(/[^0-9]/g, "")}`}
              className="text-[#25D366] hover:opacity-80 p-2"><FaWhatsapp size={18} /></a>
          </>
        )}
      </div>
    </div>
  );
}

function AddFlatDialog({ onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(""); const [block, setBlock] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/communities/${user.community_id}/flats`, { label, block: block || null });
      toast.success(`Flat ${label} added`); setLabel(""); setBlock(""); setOpen(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="add-flat-btn">
          <Building2 size={14} className="mr-1.5" /> Add flat
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">Add a flat</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div><Label>Flat number / label</Label><Input value={label} required onChange={(e) => setLabel(e.target.value)} placeholder="A-101" data-testid="flat-label" className="h-11 mt-1.5" /></div>
          <div><Label>Block (optional)</Label><Input value={block} onChange={(e) => setBlock(e.target.value)} placeholder="A" data-testid="flat-block" className="h-11 mt-1.5" /></div>
          <Button type="submit" disabled={busy} data-testid="flat-save"
            className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Adding…" : "Add flat"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditFlatDialog({ flat, users, onDone }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(flat.label); const [block, setBlock] = useState(flat.block || "");
  const [ownerId, setOwnerId] = useState(flat.owner_user_id || "");
  const [tenantId, setTenantId] = useState(flat.tenant_user_id || "");
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api.patch(`/communities/flats/${flat.id}`, {
        label, block: block || null,
        owner_user_id: ownerId || null,
        tenant_user_id: tenantId || null,
      });
      toast.success("Flat updated"); setOpen(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  const peopleOpts = (users || []).filter((u) => !["staff", "vendor", "super_admin"].includes(u.role));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-stone-400 hover:text-stone-700 p-1.5" data-testid={`flat-edit-${flat.id}`}><Edit2 size={14} /></button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">Edit flat</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid={`flat-edit-form-${flat.id}`}>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Label</Label><Input value={label} required onChange={(e) => setLabel(e.target.value)} data-testid={`flat-edit-label-${flat.id}`} className="h-11 mt-1.5" /></div>
            <div><Label>Block</Label><Input value={block} onChange={(e) => setBlock(e.target.value)} className="h-11 mt-1.5" /></div>
          </div>
          <div>
            <Label>Owner</Label>
            <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
              data-testid={`flat-edit-owner-${flat.id}`}
              className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white">
              <option value="">— Not assigned —</option>
              {peopleOpts.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <div>
            <Label>Tenant</Label>
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
              data-testid={`flat-edit-tenant-${flat.id}`}
              className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white">
              <option value="">— Not assigned —</option>
              {peopleOpts.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <p className="text-[11px] text-stone-500">Tip: to add a brand-new owner/tenant, use <em>Invite person</em> instead — it creates the account first, then attaches them to this flat.</p>
          <Button type="submit" disabled={busy} data-testid={`flat-edit-save-${flat.id}`}
            className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkFlatsDialog({ onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pattern, setPattern] = useState(""); const [busy, setBusy] = useState(false);
  function expand(p) {
    const out = new Set();
    for (const s of p.split(/[,\n]/).map((x) => x.trim()).filter(Boolean)) {
      const m = s.match(/^([A-Za-z]?-?)?(\d+)\.\.([A-Za-z]?-?)?(\d+)$/);
      if (m) for (let i = parseInt(m[2]); i <= parseInt(m[4]); i++) out.add(`${m[1] || ""}${i}`);
      else out.add(s);
    }
    return Array.from(out);
  }
  const preview = expand(pattern).slice(0, 10);
  const total = expand(pattern).length;
  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      const labels = expand(pattern);
      const { data } = await api.post(`/communities/${user.community_id}/flats/bulk`, { labels });
      toast.success(`${data.created} flats added`); setPattern(""); setOpen(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="bulk-flats-btn">
          <Plus size={14} className="mr-1.5" /> Bulk add
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">Bulk add flats</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label>Pattern or list</Label>
            <Textarea rows={4} value={pattern} onChange={(e) => setPattern(e.target.value)}
              placeholder={"A-101..A-204\nB-101..B-204"} data-testid="bulk-input"
              className="mt-1.5 font-mono text-sm" required />
            <p className="text-xs text-stone-500 mt-1">Use range like <code className="bg-stone-100 px-1 rounded">A-101..A-204</code> or comma-separated list.</p>
          </div>
          {preview.length > 0 && (
            <div className="flex flex-wrap gap-1 bg-[#FDFBF7] rounded-lg p-2 border border-stone-200">
              {preview.map((p) => <span key={p} className="text-[11px] px-2 py-0.5 bg-white border border-stone-200 rounded">{p}</span>)}
              {total > 10 && <span className="text-xs text-stone-500 self-center">+{total - 10} more</span>}
            </div>
          )}
          <Button type="submit" disabled={busy || total === 0} data-testid="bulk-save"
            className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Adding…" : `Add ${total} flat${total !== 1 ? "s" : ""}`}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InvitePersonDialog({ flats, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(""); const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); const [role, setRole] = useState("owner");
  const [flatId, setFlatId] = useState(""); const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/communities/${user.community_id}/invite`, {
        name, email, phone, role, flat_id: flatId || null,
      });
      setCreated({ ...data, name, email });
      toast.success(`Invited ${name}`); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  function reset() { setName(""); setEmail(""); setPhone(""); setRole("owner"); setFlatId(""); setCreated(null); }
  function inviteWaText() {
    const url = window.location.origin;
    return `Hi ${created.name}, you've been added to our apartment community on NivasOne.\n\nLogin: ${url}/login\nEmail: ${created.email}\nTemporary password: ${created.temp_password}\n\nPlease change your password after first login.\n\n— via NivasOne`;
  }
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-[#C05640] hover:bg-[#A54734]" data-testid="invite-btn">
          <UserPlus size={14} className="mr-1.5" /> Invite person
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">{created ? "Invite ready to share" : "Invite a person"}</DialogTitle></DialogHeader>
        {!created ? (
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={name} required onChange={(e) => setName(e.target.value)} data-testid="inv-name" className="h-11 mt-1.5" /></div>
              <div>
                <Label>Role</Label>
                <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="inv-role" className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white">
                  <option value="owner">Owner</option><option value="tenant">Tenant</option>
                  <option value="family_member">Family member</option>
                  <option value="committee">Committee</option><option value="staff">Staff</option>
                  <option value="vendor">Vendor</option>
                </select>
              </div>
            </div>
            <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="inv-email" className="h-11 mt-1.5" /></div>
            <div><Label>Phone (optional)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="inv-phone" className="h-11 mt-1.5" placeholder="+91-…" /></div>
            {(role === "owner" || role === "tenant" || role === "family_member") && (
              <div>
                <Label>Flat</Label>
                <select value={flatId} onChange={(e) => setFlatId(e.target.value)} data-testid="inv-flat" className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white">
                  <option value="">— Select flat —</option>
                  {flats.map((f) => <option key={f.id} value={f.id}>{f.label}{f.block ? ` (Block ${f.block})` : ""}</option>)}
                </select>
                {role === "family_member" && <p className="text-[11px] text-stone-500 mt-1">Family members share the flat with the owner/tenant. They can raise issues and acknowledge notices, but cannot change flat or community settings.</p>}
              </div>
            )}
            <p className="text-xs text-stone-500">A temporary password will be created. You can share it via WhatsApp on the next screen.</p>
            <Button type="submit" disabled={busy} data-testid="inv-save"
              className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Inviting…" : "Send invite"}</Button>
          </form>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="bg-[#FDFBF7] border border-[#E8D8D3] rounded-xl p-4">
              <div className="text-xs text-stone-500">Login email</div>
              <div className="font-mono text-sm">{created.email}</div>
              <div className="text-xs text-stone-500 mt-3">Temporary password</div>
              <div className="font-mono text-sm flex items-center gap-2">
                {created.temp_password}
                <button onClick={() => { navigator.clipboard.writeText(created.temp_password); toast.success("Copied"); }}
                  className="text-stone-400 hover:text-stone-700" data-testid="copy-pwd"><Copy size={14} /></button>
              </div>
            </div>
            <Button asChild className="w-full h-11 rounded-full bg-[#25D366] hover:bg-[#1EBE55] text-white" data-testid="wa-invite">
              <a href={waShareUrl(inviteWaText())} target="_blank" rel="noreferrer">
                <FaWhatsapp className="mr-2" size={18} /> Share invite on WhatsApp
              </a>
            </Button>
            <Button variant="outline" onClick={reset} className="w-full rounded-full" data-testid="invite-another">Invite another</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function TransferOwnerDialog({ flat, users, onDone }) {
  const [open, setOpen] = useState(false);
  const [newOwner, setNewOwner] = useState("");
  const [demoteTo, setDemoteTo] = useState("ex_resident");
  const [busy, setBusy] = useState(false);

  const previousOwner = users.find((u) => u.id === flat.owner_user_id);
  const eligible = users.filter((u) => u.id !== flat.owner_user_id && ["owner", "tenant", "ex_resident", "committee"].includes(u.role));

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/communities/flats/${flat.id}/transfer-owner`, {
        new_owner_user_id: newOwner,
        demote_previous_to: demoteTo,
      });
      toast.success(`Flat ${flat.label} transferred`);
      setOpen(false); setNewOwner(""); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-stone-400 hover:text-[#C05640] p-1.5" data-testid={`flat-transfer-${flat.id}`} title="Transfer owner / mark moved out">
          <ArrowLeftRight size={14} />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">Transfer flat {flat.label}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid={`transfer-form-${flat.id}`}>
          <div className="bg-stone-50 rounded-xl p-3 text-sm">
            <div className="text-xs text-stone-500">Current owner</div>
            <div className="font-medium">{previousOwner?.name || <em className="not-italic text-stone-400">— None —</em>}</div>
          </div>
          <div>
            <Label>New owner</Label>
            <select value={newOwner} required onChange={(e) => setNewOwner(e.target.value)}
              data-testid={`transfer-new-${flat.id}`}
              className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white">
              <option value="">— Pick existing member —</option>
              {eligible.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
            <p className="text-[11px] text-stone-500 mt-1">If they aren't yet a member, invite them first from this page.</p>
          </div>
          {previousOwner && (
            <div>
              <Label>Mark previous owner as</Label>
              <select value={demoteTo} onChange={(e) => setDemoteTo(e.target.value)}
                data-testid={`transfer-demote-${flat.id}`}
                className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white capitalize">
                <option value="ex_resident">Ex-resident (read-only)</option>
                <option value="tenant">Tenant of another flat</option>
                <option value="owner">Owner (multi-flat)</option>
              </select>
            </div>
          )}
          <Button type="submit" disabled={busy || !newOwner} data-testid={`transfer-save-${flat.id}`}
            className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Transferring…" : "Transfer flat"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function PersonDetailDialog({ uid }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/communities/users/${uid}`);
      setDetail(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  }
  function openIt() { if (!detail) load(); setOpen(true); }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button onClick={openIt} className="text-stone-400 hover:text-[#C05640]" data-testid={`user-detail-btn-${uid}`} title="View details">
          <Eye size={14} />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`user-detail-${uid}`}>
        <DialogHeader><DialogTitle className="font-serif-display">{detail?.name || "Member detail"}</DialogTitle></DialogHeader>
        {loading && <div className="text-stone-500 py-6 text-center text-sm">Loading…</div>}
        {detail && (
          <div className="space-y-3 mt-2 text-sm">
            <DetailRow icon={Mail} label="Email" value={detail.email} testid={`detail-email-${uid}`} />
            <DetailRow icon={Phone} label="Phone" value={detail.phone || <em className="not-italic text-stone-400">Not shared</em>} testid={`detail-phone-${uid}`} />
            <DetailRow label="Role" value={<span className="capitalize">{(detail.role || "").replace("_", " ")}</span>} testid={`detail-role-${uid}`} />
            {detail.flat_label && <DetailRow icon={Building2} label="Flat" value={detail.flat_label} testid={`detail-flat-${uid}`} />}
            {detail.bio && <DetailRow label="Note" value={detail.bio} testid={`detail-bio-${uid}`} />}
            <DetailRow icon={Calendar} label="Joined" value={new Date(detail.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} testid={`detail-joined-${uid}`} />
            {detail.last_login_at && <DetailRow icon={Calendar} label="Last login" value={new Date(detail.last_login_at).toLocaleString("en-IN")} testid={`detail-last-${uid}`} />}
            {detail.must_change_password && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">Has not yet logged in / changed password.</div>
            )}
            {detail.moved_out_at && (
              <div className="bg-stone-100 border border-stone-200 rounded-lg p-2 text-xs text-stone-700">Marked moved-out on {new Date(detail.moved_out_at).toLocaleDateString("en-IN")}.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      {Icon && <Icon size={14} className="mt-0.5 text-stone-400" />}
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-bold">{label}</div>
        <div className="text-stone-800 break-words">{value}</div>
      </div>
    </div>
  );
}
